/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import { request } from '@mairistem/tools';

import { URL_PROFESSIONS } from '@/constants';

export interface Profession {
  identifiant: number;
  libelle: string;
}

/** Au-delà, la liste n'aide plus : c'est à la recherche de trancher. */
const TAILLE_PAGE = 50;

/**
 * URL de recherche du référentiel des professions.
 *
 * ⚠️ `contains(...)` et non `like(...)` : le `like` du framework passe le
 * paramètre VERBATIM, sans joker, et ne trouve donc que les égalités exactes.
 */
export const urlRechercheProfessions = (
  recherche?: string,
  perPage = TAILLE_PAGE,
) => {
  const terme = (recherche ?? '').trim();

  return [
    URL_PROFESSIONS,
    `?perPage=${perPage}`,
    terme === '' ? '' : `&libelle=contains(${encodeURIComponent(terme)})`,
  ].join('');
};

/**
 * Charge le référentiel des professions, filtré par un terme de recherche.
 * Partagé par `ChampProfession` et `ModalProfessions`.
 */
const useProfessions = (recherche?: string, actif = true) => {
  const [professions, setProfessions] = React.useState<Profession[]>([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!actif) {
      setProfessions([]);
      return;
    }

    setLoading(true);
    request
      .get(urlRechercheProfessions(recherche))
      .then(({ data }: { data?: Profession[] }) => {
        setProfessions(data ?? []);
      })
      .catch(() => {
        // Un référentiel indisponible ne bloque pas la saisie : le champ reste
        // libre.
        setProfessions([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [recherche, actif]);

  /** Casse ignorée : sert à ne pas proposer « Créer » sur un doublon. */
  const correspondExactement = React.useCallback(
    (terme?: string) =>
      professions.some(
        (profession) =>
          _.toUpper(profession.libelle.trim()) ===
          _.toUpper((terme ?? '').trim()),
      ),
    [professions],
  );

  return { professions, loading, correspondExactement };
};

export default useProfessions;
